import { Database } from "@punchup/data/supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import dayjs from "dayjs";
import jwt from "jsonwebtoken";

export type Livestream = Database["public"]["Tables"]["livestreams"]["Row"];
export type LivestreamState = Livestream["state"];

export type LivestreamAndToken = {
  livestream: Livestream;
  signedVideoToken: string | null;
};

export async function getLivestreamForVenuePage(
  supabase: SupabaseClient<Database>,
  venuePageId: string
): Promise<Livestream | null> {
  const { data } = await supabase
    .from("livestreams")
    .select("*")
    .eq("venue_page_id", venuePageId)
    .in("state", ["live", "waiting"])
    .maybeSingle();

  return data;
}

export async function getLivestreamById(
  supabase: SupabaseClient<Database>,
  livestreamId: string
): Promise<Livestream | null> {
  const { data } = await supabase
    .from("livestreams")
    .select("*")
    .eq("id", livestreamId)
    .maybeSingle();
  return data;
}

export async function getLivestreamStateById(
  supabase: SupabaseClient<Database>,
  livestreamId: string
): Promise<LivestreamState | null> {
  const { data } = await supabase
    .from("livestreams")
    .select("state")
    .eq("id", livestreamId)
    .maybeSingle();
  return data?.state || null;
}

/**
 * Get a signed token for a viewing a livestream.
 *
 * Only call this function on the server! The vendor account keys are
 * sensitive and should not be exposed to the client.
 *
 * Right now this assumes a Mux livestream or will throw an error.
 */
export async function getTokenForLivestream(
  supabase: SupabaseClient<Database>,
  videoId: string,
  vendorAccountId: string
): Promise<string | null> {
  if (typeof window !== "undefined") {
    throw new Error("This function must be called on the server");
  }

  const { data: vendorAccount } = await supabase
    .from("livestream_vendor_accounts")
    .select("*")
    .eq("id", vendorAccountId)
    .throwOnError()
    .single();

  if (!vendorAccount) {
    throw new Error("Vendor account not found");
  }

  if (vendorAccount.vendor_type !== "mux") {
    throw new Error("Unsupported vendor type: " + vendorAccount.vendor_type);
  }

  return jwt.sign(
    {
      sub: videoId,
      aud: "v",
      exp: dayjs().add(1, "day").unix(),
      kid: vendorAccount.signing_key_id,
    },
    Buffer.from(vendorAccount.signing_key_secret, "base64"),
    { algorithm: "RS256" }
  );
}
